import {
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    makeStyles,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from "@material-ui/core";
import { Add, Edit, InfoOutlined, MoreVert, Settings } from "@material-ui/icons";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as ROUTES from "../../constants/routes";
import * as COMS from "../../constants/comsTypes";
import DeviceSetupForm from "../DeviceSetupForm";
import { AuthUserContext } from "../Session";
import AddDevice from "../AddDevice";
import { hasPermission } from "../../functions/authorization";
import { DEVICE_ADD, DEVICE_SELECT, DEVICE_UPDATE, GROUPS_UPDATE} from "../../constants/roles";
import * as CONF from "../../constants/conf";

const GroupCard = ({ data, edit, index, readOnly = false }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const history = useHistory();
    const authUser = useContext(AuthUserContext);

    const [anchorEl, setAnchorEl] = useState(null);
    const [deviceSetupOpen, setDeviceSetupOpen] = useState(false);
    const [openAddDevice, setOpenAddDevice] = useState(false);

    const canEditGroup = hasPermission(authUser, GROUPS_UPDATE);
    const canAddDevice = hasPermission(authUser, DEVICE_ADD);
    const canViewDevices = hasPermission(authUser, DEVICE_SELECT);
    const canConfigureDevices = hasPermission(authUser, DEVICE_UPDATE) || hasPermission(authUser, GROUPS_UPDATE);

    const isEmtProgram = data?.program_type?.toString() === CONF.DEVICE_PROGRAM_TYPE_EMT?.toString();
    

    const getProgramDisplayName = () => {
        const programId = data?.programid || data?.program_id;

        const programFromUser =
            (authUser?.program || []).find((program) => program.id === programId) ||
            (authUser?.programas || []).find((program) => program.id === programId);

        return programFromUser?.name || "";
    };

    const getCoordinatorDisplayItems = () => {
        const programDisplayName = getProgramDisplayName();
        const isEmtProgram = programDisplayName.toLowerCase().includes("emt");

        if (isEmtProgram && programDisplayName) {
            return [
                {
                    primary: programDisplayName,
                    secondary: "",
                },
            ];
        }

        return (data?.coordinators || []).map((coordinator) => ({
            primary: `${coordinator.name || ""} ${coordinator.lastname || ""}`.trim(),
            secondary: coordinator.user_name,
        }));
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleEditGroup = () => {
        if (!canEditGroup) return;

        handleClose();
        edit(data.groupid);
    };

    const handleAddDevice = () => {
        if (!canAddDevice) return;

        handleClose();
        setOpenAddDevice(true);
    };

    const handleOpenDeviceSetup = () => {
        if (!canConfigureDevices) return;

        handleClose();
        setDeviceSetupOpen(true);
    };

    const handleViewDevices = () => {
        if (!canViewDevices) return;

        window.scrollTo(0, 0);
        history.push(`${ROUTES.PROGRAMS}/${data.programid}${ROUTES.GROUPS}/${data.groupid}${ROUTES.DEVICES}`);
    };

    return (
        <Card className={classes.card} id={`grCard${index}`} variant="outlined" elevation={0} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CardContent style={{ flex: 1 }}>
                <Grid container alignItems="center">
                    <Grid container direction="row" justifyContent="space-between">
                        <Grid item xs={10}>
                            <Typography variant="h5" component="h2" color="secondary" style={{ marginBottom: 0, whiteSpace: "normal", fontWeight: 500 }}>
                                {data.name}
                            </Typography>
                            <Typography variant="subtitle1" color="textSecondary">
                                {data.def}
                            </Typography>
                        </Grid>

                        <Grid item xs={2} container alignContent="flex-start" justifyContent="flex-end">
                            <IconButton color="secondary" aria-label="group-actions" onClick={handleClick}>
                                <MoreVert />
                            </IconButton>
                            <Menu id="simple-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
                                <MenuItem disabled={!canEditGroup} onClick={handleEditGroup}>
                                    <Edit color={canEditGroup ? "primary" : "disabled"} style={{ marginRight: 9 }} />
                                    {t("common.edit")}
                                </MenuItem>

                                <MenuItem disabled={!canAddDevice} onClick={handleAddDevice}>
                                    <Add color={canAddDevice ? "primary" : "disabled"} style={{ marginRight: 9 }} />
                                    {t("devices.toolbar.tooltip.addDevice")}
                                </MenuItem>

                                <MenuItem disabled={!canConfigureDevices} onClick={handleOpenDeviceSetup}>
                                    <Settings color={canConfigureDevices ? "primary" : "disabled"} style={{ marginRight: 9 }} />
                                    {t("group.r100Conf.button")}
                                </MenuItem>
                            </Menu>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" style={{ marginTop: 16, alignContent: "center", display: "flex", fontWeight: 600 }}>
                                {t("GourCard.Coms")}
                                <Tooltip
                                    interactive
                                    placement="top"
                                    classes={{
                                        tooltip: {
                                            maxWidth: 500,
                                        },
                                    }}
                                    title={
                                        <Typography>
                                            {t("groudCard.typeComsDesc")}
                                            {COMS.STANDALONE}
                                        </Typography>
                                    }
                                >
                                    <InfoOutlined fontSize="small" color="primary" style={{ marginLeft: 6 }} />
                                </Tooltip>
                            </Typography>

                            {data?.comsTypes?.map((v, i) => (
                                <Chip label={v} key={i} variant="default" size="small" color="secondary" style={{ borderRadius: 9, marginTop: 6, marginRight: 6 }} />
                            ))}
                            {(!data?.comsTypes || data?.comsTypes?.length === 0) && (
                                <Chip label={t("unassigned")} variant="outlined" size="small" color="secondary" style={{ borderRadius: 9, marginTop: 6, marginRight: 6 }} />
                            )}
                        </Grid>
                    </Grid>

                    <Grid style={{ flex: 1 }}>
                        <List
                            dense
                            subheader={
                                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                    {t("groups.card.coordinators")}
                                </Typography>
                            }
                            style={{ marginTop: 16, fontWeight: 600 }}
                        >
                            {getCoordinatorDisplayItems().map((item, i) => (
                                <ListItem key={i} style={{ padding: 0 }}>
                                    <ListItemText primary={item.primary} secondary={item.secondary} />
                                </ListItem>
                            ))}
                        </List>
                    </Grid>
                </Grid>
            </CardContent>

            <Grid container justifyContent="flex-end" style={{ width: "100%" }}>
                <Tooltip
                    title={
                        !canViewDevices ? (
                            <Typography variant="body1" style={{ fontSize: 12 }}>
                                {t("permissions.noPermission")}
                            </Typography>
                        ) : (
                            ""
                        )
                    }
                >
                    <span>
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={!canViewDevices}
                            style={{
                                margin: 9,
                                marginRight: 12,
                                marginBottom: 12,
                                textTransform: "none",
                                borderRadius: 16,
                                minWidth: 84,
                            }}
                            onClick={handleViewDevices}
                        >
                            {t("groupsCard.ViewDevices", { number: data.devicesnumber })}
                        </Button>
                    </span>
                </Tooltip>
            </Grid>

            {canAddDevice && <AddDevice open={openAddDevice} handleClose={setOpenAddDevice} authUser={authUser} selectedGroupID={data.groupid} />}

            {canConfigureDevices && deviceSetupOpen && (
                <DeviceSetupForm
                    open={deviceSetupOpen}
                    handleClose={setDeviceSetupOpen}
                    groupId={data?.groupid}
                    readOnly={readOnly}
                    isEmtProgram={isEmtProgram}
                />
            )}
        </Card>
    );
};

export default GroupCard;

const useStyles = makeStyles((theme) => ({
    card: {
        borderRadius: 12,
        background: "#f7f8fa",
    },
    media: {
        height: 140,
    },
    expand: {
        transform: "rotate(0deg)",
        marginLeft: "auto",
        transition: theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest,
        }),
    },
    expandOpen: {
        transform: "rotate(180deg)",
    },
    chip: {
        margin: theme.spacing(0.5),
    },
}));
